import React from 'react';
import { Modal } from 'playup-web-core';
import { useLocation } from 'react-router-dom';

import { DepositPage, PaymentPages } from './USDepositService';

import { getDepositRequestTypes } from '../../../api/users';
import useAsyncAction from '../../../hooks/useAsyncAction';

import USDepositPaymentSelection from './USDepositPaymentSelection';
import USDepositAmount from './USDepositAmount';
import ResponsibleGambling from '../../../User/components/ResponsibleGambling';
import USDepositSuccessfulModal from './USDepositSuccessfulModal';
import USDepositErrorModal from './USDepositErrorModal';
import PortalSessionContext from '../../../contexts/PortalSessionContext/PortalSessionContext';
import useNotify from '../../../hooks/useNotify';

const USDepositModal = (props: Props) => {
	const { user } = React.useContext(PortalSessionContext);
	const notify = useNotify();
	const location = useLocation();
	const [loading] = useAsyncAction();

	const [depositLimits, setDepositLimits] = React.useState<DepositLimits>({
		min_deposit: 100,
		max_deposit: 50000000,
		min_blueshyft_deposit: 1000,
		max_blueshyft_deposit: 800000,
	});
	const [activeDepositLimit, setActiveDepositLimit] = React.useState<DepositLimit>({
		min: depositLimits.min_deposit,
		max: depositLimits.max_deposit,
	});
	const [depositAmount, setDepositAmount] = React.useState<number>(0); // In cents
	const [currentPageIndex, setCurrentPageIndex] = React.useState<number>(0);
	const [availablePaymentMethodIds, setAvailablePaymentMethodIds] = React.useState<Array<string>>([]);

	const state = location.state as DepositState;
	const getDefaultAmount = () => {
		if (state?.defaultAmount !== undefined) {
			return state?.defaultAmount >= depositLimits.min_deposit
				? state?.defaultAmount / 100
				: depositLimits.min_deposit / 100;
		}
		return undefined;
	};
	const defaultAmount = getDefaultAmount();

	const onDepositClick = () => {
		setCurrentPageIndex(PaymentPages.Select);
	};

	const onDepositSuccessful = () => {
		setCurrentPageIndex(PaymentPages.DepositSuccessful);
	};

	const onDepositError = () => {
		setCurrentPageIndex(PaymentPages.DepositError);
	};

	const getBackToHomePage = () => {
		setCurrentPageIndex(PaymentPages.Home);
	};

	const getDepositData = async (firstRun?: boolean) => {
		const depositRequestTypes = await getDepositRequestTypes();

		setAvailablePaymentMethodIds(Object.keys(depositRequestTypes.meta.deposit_request_types));

		if (depositRequestTypes.meta.min_deposit && depositRequestTypes.meta.max_deposit) {
			setDepositLimits({
				min_deposit: depositRequestTypes.meta.min_deposit,
				max_deposit: depositRequestTypes.meta.max_deposit,
				min_blueshyft_deposit: depositRequestTypes.meta.min_blueshyft_deposit,
				max_blueshyft_deposit: depositRequestTypes.meta.max_blueshyft_deposit,
			});
			setActiveDepositLimit({
				min: depositRequestTypes.meta.min_deposit,
				max: depositRequestTypes.meta.max_deposit,
			});
		}
	};

	React.useEffect(() => {
		if (!user?.id_verified) {
			notify({
				type: 'danger',
				title: 'Error',
				message: 'You need to verify your ID to make a deposit',
				timeout: 10000,
			});
		}
	}, [notify, user?.id_verified]);

	React.useEffect(() => {
		getDepositData();
	}, []);

	const HomePage: DepositPage = {
		pageIndex: PaymentPages.Home,
		subtitle: 'Deposit',
		previousPageIndex: undefined,
		body: (
			<USDepositAmount
				loading={loading}
				defaultAmount={defaultAmount}
				activeDepositLimit={activeDepositLimit}
				onDepositClick={onDepositClick}
				setDepositAmount={setDepositAmount}
			/>
		),
	};

	const SelectPage: DepositPage = {
		pageIndex: PaymentPages.Select,
		subtitle: 'Add New Payment Method',
		previousPageIndex: PaymentPages.Home,
		body: (
			<USDepositPaymentSelection
				depositAmount={depositAmount}
				availablePaymentMethodIds={availablePaymentMethodIds}
				getBackToHomePage={getBackToHomePage}
				onDepositSuccessful={onDepositSuccessful}
				onDepositError={onDepositError}
			/>
		),
	};

	const DepositSuccessfulPage: DepositPage = {
		pageIndex: PaymentPages.DepositSuccessful,
		subtitle: 'Deposit Successful',
		previousPageIndex: PaymentPages.Home,
		body: <USDepositSuccessfulModal depositAmount={depositAmount} closeDepositModal={props.onClose} />,
	};

	const DepositErrorPage: DepositPage = {
		pageIndex: PaymentPages.DepositError,
		subtitle: 'Deposit Error',
		previousPageIndex: PaymentPages.Home,
		body: <USDepositErrorModal getBackToHomePage={getBackToHomePage} />,
	};

	const pages = [HomePage, SelectPage, DepositSuccessfulPage, DepositErrorPage];
	const getPage = (pageIndexToFind: number) => {
		return pages.find((x) => x.pageIndex === pageIndexToFind);
	};
	const currentPage = getPage(currentPageIndex);

	return (
		<Modal
			title={currentPage?.subtitle || 'Deposit'}
			onClose={props.onClose}
			onBack={
				currentPage?.previousPageIndex !== undefined
					? () => {
							setCurrentPageIndex(currentPage.previousPageIndex as number);
					  }
					: undefined
			}
		>
			{currentPage?.pageIndex === PaymentPages.Home ? (
				<USDepositAmount
					loading={loading}
					defaultAmount={defaultAmount}
					activeDepositLimit={activeDepositLimit}
					onDepositClick={onDepositClick}
					setDepositAmount={setDepositAmount}
				/>
			) : (
				currentPage?.body
			)}
			{/* this disgusting ternary is used to deal with react hook form register */}
			{props.showRG && <ResponsibleGambling />}
		</Modal>
	);
};

export default USDepositModal;

type DepositState = {
	defaultAmount: number;
	warningMessage: string;
};

export type DepositLimit = {
	min: number;
	max: number;
};

interface Props {
	onClose: () => void;
	showRG?: boolean;
}
